import { useEffect, useState } from 'react';
import { api, CANALES, type CanalesYearResponse, type CanalTotal } from '../lib/api.js';
import { fmtInt, fmtPct, fmtUsd } from '../lib/format.js';
import { KpiCard } from './KpiCard.js';
import { ChartCard } from './charts/ChartCard.js';
import { CanalesMensualChart } from './charts/CanalesMensualChart.js';
import { CanalesMixBar } from './charts/CanalesMixBar.js';
import { CanalesSedeMatrix } from './charts/CanalesSedeMatrix.js';
import { ExpediaRoomTypes } from './charts/ExpediaRoomTypes.js';

const MES_CORTO = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

/**
 * Panel de CANALES DE VENTA: noches que entran por Central de Reservas, VIP,
 * Ventas y Front Desk, del reporte "Reservation Statistics · Option Source".
 *
 * Es ANUAL y no por rango, como el panel Diamond y el de cuentas: el reporte
 * nace en grano de mes y el rango por defecto del dashboard (mes en curso) no
 * contiene ningún mes completo. El año sale del filtro de fechas; las sedes, del
 * filtro de sedes.
 */
export function CanalesPanel({ anio, sedesParam }: { anio: number; sedesParam?: string }) {
  const [data, setData] = useState<CanalesYearResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Mes del mix de la derecha; null = el año completo. */
  const [mesMix, setMesMix] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    // Otro año u otras sedes traen otros meses cargados: el mes elegido antes
    // puede no existir en el dato nuevo, así que se vuelve al año.
    setMesMix(null);
    api
      .canalesYear({ anio, sedes: sedesParam })
      .then((r) => alive && setData(r))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Error cargando canales'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [anio, sedesParam]);

  if (loading) return <p className="text-sm text-slate-400">Cargando canales…</p>;
  if (error) return <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>;
  if (!data) return null;

  if (data.sinDatos) {
    return (
      <ChartCard title={`Canales de venta · ${anio}`}>
        <p className="text-sm text-slate-500">
          Sin noches por canal para {anio}. Se alimentan del reporte «Reservation Statistics» por
          Source (uno por sede y mes), que se sube en Ingesta.
        </p>
      </ChartCard>
    );
  }

  const ytdHint = `YTD a ${MES_CORTO[data.hastaMes - 1]} vs ${anio - 1}`;
  const exp = data.expedia;

  // --- Mix de la derecha: año completo o un mes suelto ----------------------
  // Solo se ofrecen los meses CON DATO; el share se recalcula sobre el total de
  // ese mes, no sobre el del año, o los tramos no sumarían 100 %.
  const mesesConDato = data.meses.filter((m) => m.conDato);
  const mesPunto = mesesConDato.find((m) => m.mes === mesMix) ?? null;
  const mixPorCanal: CanalTotal[] = mesPunto
    ? CANALES.map((canal) => {
        const noches = mesPunto.porCanal[canal] ?? 0;
        return { canal, noches, share: mesPunto.total > 0 ? noches / mesPunto.total : null };
      })
    : data.porCanal;
  const mixTotal = mesPunto ? mesPunto.total : data.total;
  const mixDesc = mesPunto ? `${MES_CORTO[mesPunto.mes - 1]} ${anio}` : `${anio}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          Canales de venta · {anio}
        </h2>
        <span className="text-xs text-slate-500">
          Noches de habitación por canal (reporte por Source) · {ytdHint}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          label="Central de Reservas"
          kpi={data.cards.nochesCdr}
          format={fmtInt}
          hint={ytdHint}
        />
        <KpiCard label="Ventas" kpi={data.cards.nochesVta} format={fmtInt} hint={ytdHint} />
        <KpiCard label="Front Desk" kpi={data.cards.nochesFro} format={fmtInt} hint={ytdHint} />
        <KpiCard label="VIP" kpi={data.cards.nochesVip} format={fmtInt} hint={ytdHint} />
        <KpiCard
          label="Expedia"
          kpi={data.cards.nochesExp}
          format={fmtInt}
          hint={
            exp.sinDatos
              ? 'Sin reporte de Expedia cargado'
              : `${fmtInt(exp.reservas)} reservas · ADR ${exp.adrUsd == null ? '—' : fmtUsd(exp.adrUsd)}`
          }
        />
      </div>

      {/* Un descuadre es un fallo de LECTURA del reporte, no un mes flojo: se
          muestra arriba del dato para que nadie interprete lo que hay debajo
          antes de saber que le faltan noches. */}
      {data.descuadres.length > 0 && (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
          {data.descuadres.length} reporte(s) no cuadran con su propio Grand Total:{' '}
          {data.descuadres
            .map(
              (d) =>
                `${d.sede} ${d.anio}-${String(d.mes).padStart(2, '0')} (${fmtInt(d.suma)} vs ${fmtInt(d.grandTotal)})`,
            )
            .join(' · ')}
          . Faltan o sobran filas en la carga de ese mes.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={`Peso de cada canal por mes · ${anio}`}>
          <CanalesMensualChart meses={data.meses} />
        </ChartCard>

        <ChartCard
          title={`Mix de canales · ${mixDesc} (${fmtInt(mixTotal)} noches)`}
          action={
            mesesConDato.length > 0 && (
              <div className="inline-flex overflow-hidden rounded-md border border-slate-700 text-xs">
                <button
                  onClick={() => setMesMix(null)}
                  className={`px-2 py-1 ${
                    mesPunto == null ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-300'
                  }`}
                >
                  Año
                </button>
                {mesesConDato.map((m) => (
                  <button
                    key={m.mes}
                    onClick={() => setMesMix(m.mes)}
                    className={`px-2 py-1 ${
                      mesPunto?.mes === m.mes
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-900 text-slate-300'
                    }`}
                  >
                    {MES_CORTO[m.mes - 1]}
                  </button>
                ))}
              </div>
            )
          }
        >
          <CanalesMixBar porCanal={mixPorCanal} total={mixTotal} />
          {data.desconocidos.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              «Otros» son códigos sin canal asignado (conteo del año {anio}):{' '}
              {data.desconocidos
                .slice(0, 6)
                .map((d) => `${d.source} (${fmtInt(d.noches)})`)
                .join(', ')}
              {data.desconocidos.length > 6 ? '…' : ''}. Si alguno debe contar como un canal, hay
              que declararlo en el mapa de equivalencias.
            </p>
          )}
        </ChartCard>
      </div>

      {!exp.sinDatos && (
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title={`Expedia · tipos de habitación vendidos · ${anio}`}>
            <ExpediaRoomTypes rows={exp.roomTypes} />
          </ChartCard>

          <ChartCard title={`Expedia · el canal en números · ${anio}`}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Noches</dt>
                <dd className="text-xl font-semibold tabular-nums text-slate-50">
                  {fmtInt(exp.noches)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Reservas</dt>
                <dd className="text-xl font-semibold tabular-nums text-slate-50">
                  {fmtInt(exp.reservas)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">ADR del canal</dt>
                <dd className="text-xl font-semibold tabular-nums text-slate-50">
                  {exp.adrUsd == null ? '—' : fmtUsd(exp.adrUsd)}
                </dd>
                <dd className="text-[11px] text-slate-500">{fmtUsd(exp.importeUsd)} facturados</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Cancelación</dt>
                <dd className="text-xl font-semibold tabular-nums text-slate-50">
                  {exp.tasaCancelacion == null ? '—' : fmtPct(exp.tasaCancelacion * 100)}
                </dd>
                <dd className="text-[11px] text-slate-500">
                  {fmtInt(exp.canceladas)} reservas canceladas
                </dd>
              </div>
            </dl>
            <p className="mt-4 border-t border-slate-700 pt-3 text-xs text-slate-500">
              Las noches de Expedia ya venían dentro del total del hotel, repartidas entre los
              códigos residuales del PMS, así que aquí se <strong>restan de «Otros»</strong> en vez
              de sumarse encima: el mix sigue cerrando en el 100 % de lo vendido.
            </p>
          </ChartCard>
        </div>
      )}

      {/* Expedia por hotel. Cada sede lleva SUS ratios recalculados: heredar el
          ADR o la cancelación de la cadena respondería a otra pregunta. */}
      {!exp.sinDatos && exp.porSede.length > 0 && (
        <ChartCard title={`Expedia por hotel · ${anio}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Hotel</th>
                  <th className="px-3 py-2 text-right">Noches</th>
                  <th className="px-3 py-2 text-right">% del canal</th>
                  <th className="px-3 py-2 text-right">Reservas</th>
                  <th className="px-3 py-2 text-right">Cancelación</th>
                  <th className="px-3 py-2 text-right">ADR</th>
                  <th className="px-3 py-2 text-right">Facturado</th>
                  <th className="px-3 py-2">Tipo más vendido</th>
                </tr>
              </thead>
              <tbody>
                {exp.porSede.map((s) => (
                  <tr key={s.sede} className="border-t border-slate-700/60">
                    <td className="px-3 py-1.5 font-medium text-slate-200">
                      {s.sede}
                      <span className="block text-[11px] text-slate-500">
                        {s.mesesConDato} mes(es) con dato
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                      {fmtInt(s.noches)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                      {s.share == null ? '—' : fmtPct(s.share * 100)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                      {fmtInt(s.reservas)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                      {s.tasaCancelacion == null ? '—' : fmtPct(s.tasaCancelacion * 100)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                      {s.adrUsd == null ? '—' : fmtUsd(s.adrUsd)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                      {fmtUsd(s.importeUsd)}
                    </td>
                    <td className="px-3 py-1.5 text-slate-400">
                      {s.roomTypes[0]
                        ? `${s.roomTypes[0].roomType} (${fmtInt(s.roomTypes[0].noches)})`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-600 font-medium">
                  <td className="px-3 py-1.5 text-slate-200">TOTAL</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                    {fmtInt(exp.noches)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                    {exp.noches > 0 ? fmtPct(100) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                    {fmtInt(exp.reservas)}
                  </td>
                  {/* Recalculada sobre la cadena, no es la media de las sedes. */}
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                    {exp.tasaCancelacion == null ? '—' : fmtPct(exp.tasaCancelacion * 100)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                    {exp.adrUsd == null ? '—' : fmtUsd(exp.adrUsd)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                    {fmtUsd(exp.importeUsd)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mt-3 border-t border-slate-700 pt-3 text-xs text-slate-500">
            El ADR y la cancelación de cada hotel se recalculan sobre sus propias cifras; la fila
            TOTAL hace lo mismo sobre la cadena, así que <strong>no</strong> es el promedio de las
            filas de arriba. Las sedes pueden ir desfasadas: cada una dice cuántos meses aporta.
          </p>
        </ChartCard>
      )}

      {/* Si Expedia no cabe en el «Otros» de un mes, el supuesto del tallado se
          rompe ahí: se dice cuál y cuánto, en vez de recortar en silencio. */}
      {exp.exceso.length > 0 && (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
          En {exp.exceso.length} caso(s) Expedia supera el «Otros» del PMS y solo entra la parte que
          cabe:{' '}
          {exp.exceso
            .map(
              (x) =>
                `${x.sede} ${x.anio}-${String(x.mes).padStart(2, '0')} (${fmtInt(x.expedia)} vs ${fmtInt(x.otros)})`,
            )
            .join(' · ')}
          . O el PMS clasifica esas reservas fuera de los códigos residuales, o falta cargar ese
          mes.
        </div>
      )}
      {exp.sinBasePms > 0 && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2 text-xs text-slate-400">
          {exp.sinBasePms} mes(es) con Expedia y sin reporte del PMS: esas noches entran enteras al
          mix porque no hay de dónde restarlas.
        </div>
      )}

      <ChartCard title={`Canal × sede · ${anio} (noches y peso dentro del hotel)`}>
        <CanalesSedeMatrix rows={data.matriz} />
      </ChartCard>
    </div>
  );
}
